// v2/layout/Breadcrumbs.tsx — Contextual breadcrumb navigation

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

/** Map route segments to human-readable labels */
const SEGMENT_LABELS: Record<string, string> = {
  v2: 'Home',
  leads: 'Leads',
  applications: 'Applications',
  tasks: 'Tasks',
  groups: 'Groups',
  'login-history': 'Login History',
  settings: 'Settings',
  new: 'New',
  'residence-verification': 'Residence Verification',
  create: 'Create',
  documents: 'Documents',
  'add-member': 'Add Member',
  view: 'View',
};

interface Crumb {
  label: string;
  path: string;
}

function buildCrumbs(pathname: string): Crumb[] {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: Crumb[] = [];

  // Always start with Home pointing to /v2/
  crumbs.push({ label: 'Home', path: '/v2/' });

  let accumulated = '';
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    accumulated += `/${seg}`;

    // Skip the "v2" segment — already represented by Home
    if (seg === 'v2') continue;

    const label = SEGMENT_LABELS[seg] ?? seg;
    crumbs.push({ label, path: accumulated });
  }

  return crumbs;
}

export function Breadcrumbs(): React.ReactElement {
  const { pathname } = useLocation();
  const crumbs = buildCrumbs(pathname);

  // Don't render breadcrumbs if we're on the home page
  if (crumbs.length <= 1) return <></>;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-muted-foreground px-4 py-2 md:px-6 overflow-x-auto scrollbar-none border-b border-border/50">
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        return (
          <React.Fragment key={crumb.path}>
            {index > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />}
            {index === 0 ? (
              <Link to={crumb.path} className="flex items-center gap-1 hover:text-foreground transition-colors shrink-0">
                <Home className="h-3.5 w-3.5" />
                <span className="sr-only">Home</span>
              </Link>
            ) : isLast ? (
              <span className="font-medium text-foreground truncate max-w-[150px] sm:max-w-[200px] shrink-0">{crumb.label}</span>
            ) : (
              <Link to={crumb.path} className="hover:text-foreground transition-colors truncate max-w-[120px] sm:max-w-[200px] shrink-0 whitespace-nowrap">
                {crumb.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
